import { useCallback, useEffect, useState, useContext } from "react";
import { MdModeEdit, MdOutlineDelete } from "react-icons/md";
import { IoMdAdd, IoIosCloseCircleOutline } from "react-icons/io";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import { DarkModeContext } from "../../Context/DarkModeContext.jsx";
import axiosInstance from "../../Context/axiosInstance.js";
import TreeView from "../../Components/TreeView";
import { findFolderParentId } from "../../utils/folderTree";

function AdminFolders() {
  const { isDarkMode } = useContext(DarkModeContext);

  // Folder state
  const [folders, setFolders] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Subfolders state
  const [openedFolderContents, setOpenedFolderContents] = useState([]);
  const [loadingFolderContents, setLoadingFolderContents] = useState(false);
  const [isFolderContentsModalOpen, setFolderContentsModalOpen] =
    useState(false);

  const [editingFolder, setEditingFolder] = useState(null);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmFolderDeleteModalOpen, setConfirmFolderDeleteModalOpen] =
    useState(false);
  const [folderIdToDelete, setFolderIdToDelete] = useState(null);

  // Tree states
  const [folderTree, setFolderTree] = useState([]);
  const [folderTreesCache, setFolderTreesCache] = useState({});

  // FOLDER CONTENT MODAL STATES
  const [openedFolder, setOpenedFolder] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  // Add Folder States
  const [addFolderForm, setAddFolderForm] = useState({
    name: "",
    storeId: 0,
    parentFolderId: 0,
  });

  // Edit Folder States
  const [editFolderForm, setEditFolderForm] = useState({
    name: "",
    storeId: 0,
    parentFolderId: 0,
  });
  const [loadingFolderTree, setLoadingFolderTree] = useState(false);

  const [loadingAddFolder, setLoadingAddFolder] = useState(false);
  const [loadingEditFolder, setLoadingEditFolder] = useState(false);
  const [editTreeSelection, setEditTreeSelection] = useState(null);
  const [stores, setStores] = useState([]);

  // Pagination + sorting
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState("Name");
  const [sortDirection, setSortDirection] = useState("desc");
  const [selectedStoreIdTable, setSelectedStoreIdTable] = useState(1);

  // Modals
  const [isFolderModalOpen, setFolderModalOpen] = useState(false);
  const [animationClass, setAnimationClass] = useState("");

  const navigate = useNavigate();

  // fetchStores
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const res = await axiosInstance.get(
          `/stores?page=1&pageSize=100000&sortColumn=Name&sortDirection=asc&search=`
        );
        setStores(res.data.response || []);
      } catch (err) {
        toast.error("Failed to fetch stores");
      }
    };
    fetchStores();
  }, []);

  // Fetch all folders
  const fetchMainFolders = useCallback(async () => {
    if (!selectedStoreIdTable || selectedStoreIdTable <= 0) return;
    setLoading(true);

    try {
      const res = await axiosInstance.get(
        `/folders?page=${page}&pageSize=${pageSize}&sortColumn=${sortColumn}&sortDirection=${sortDirection}&search=${encodeURIComponent(
          searchQuery
        )}&storeId=${selectedStoreIdTable}`
      );

      const fetchedFolders = res.data.response.map((f) => ({
        ...f,
        totalCourses: f.coursesCount ?? 0, // direct use from API
      }));

      setFolders(fetchedFolders);
      setTotalPages(Math.ceil(res.data.totalCount / pageSize));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    pageSize,
    sortColumn,
    sortDirection,
    searchQuery,
    selectedStoreIdTable,
  ]);

  useEffect(() => {
    fetchMainFolders();
    // Clear selected courses and update UI
    setSelectedFolders([]);
  }, [selectedStoreIdTable, fetchMainFolders]);

  // Fetch the folder tree
  const fetchFolderTree = async (storeId, forceRefresh = false) => {
    // If cached and no refresh requested, return cache
    if (!forceRefresh && folderTreesCache[storeId]) {
      return folderTreesCache[storeId];
    }

    try {
      const res = await axiosInstance.get(`/folders/tree?storeId=${storeId}`);
      setFolderTreesCache((prev) => ({ ...prev, [storeId]: res.data }));
      return res.data;
    } catch (err) {
      toast.error("Failed to load folder tree");
      return [];
    }
  };

  // Add Folder
  const handleAddFolder = async (e) => {
    e.preventDefault();

    if (!addFolderForm.name.trim())
      return toast.error("Folder name is required");
    if (!addFolderForm.storeId || addFolderForm.storeId <= 0)
      return toast.error("Please select a store");

    setLoadingAddFolder(true);
    try {
      await axiosInstance.post("/folders", {
        name: addFolderForm.name,
        storeId: addFolderForm.storeId,
        parentFolderId:
          addFolderForm.parentFolderId === 0
            ? null
            : addFolderForm.parentFolderId,
      });

      toast.success("Folder added successfully");
      setAddFolderForm({ name: "", storeId: 0, parentFolderId: 0 });

      // Fetch main folder list
      fetchMainFolders();

      // **Force refresh the folder tree for this store**
      const updatedTree = await fetchFolderTree(addFolderForm.storeId, true);
      setFolderTree(updatedTree);
      handleCloseAddFolderModal();
    } catch (err) {
      toast.error(
        err.response?.data?.errors?.[0]?.reason || "Failed to add folder"
      );
    } finally {
      setLoadingAddFolder(false);
    }
  };

  const handleFolderModalOpen = async () => {
    setFolderModalOpen(true);
    setAnimationClass("slide-in");

    // Only fetch if a store is selected
    if (addFolderForm.storeId) {
      const tree = await fetchFolderTree(addFolderForm.storeId);
      setFolderTree(tree); // Update state explicitly
    } else {
      setFolderTree([]); // clear tree if no store selected
    }
  };

  // Called when the user selects a store (e.g., from dropdown)
  const handleStoreChange = async (storeId, formType = "add") => {
    if (formType === "add") {
      setAddFolderForm((prev) => ({
        ...prev,
        storeId,
        parentFolderId: 0, // reset folder selection
      }));
    } else if (formType === "edit") {
      setEditTreeSelection(null);
      setEditFolderForm((prev) => ({
        ...prev,
        storeId,
        parentFolderId: 0, // reset folder selection
      }));
    }

    // Fetch the tree for that store
    const tree = await fetchFolderTree(storeId);
    setFolderTree(tree);
  };

  const toggleSortDirection = (column) => {
    const validColumns = [
      "Id",
      "Name",
      "StoreName",
      "SubFoldersCount",
      // "CoursesCount",
    ];
    if (!validColumns.includes(column)) return;
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      navigate(`?page=${newPage}&pageSize=${pageSize}`);
    }
  };

  // Open Edit modal for a specific folder
  const handleEditFolderModalOpen = async (folder) => {
    if (!folder) return;
    const storeId =
      folder.storeId ??
      folders.find((f) => f.id === folder.parentFolderId)?.storeId ??
      selectedStoreIdTable;

    setEditingFolder(folder);
    setEditTreeSelection(folder.id);

    setEditFolderForm({
      name: folder.name,
      storeId,
      parentFolderId: folder.parentFolderId || null,
    });

    // Open modal immediately
    setAnimationClass(isFolderModalOpen ? "slide-in-right" : "slide-in");
    setEditModalOpen(true);

    // Now fetch folder tree in background
    if (storeId > 0) {
      try {
        setLoadingFolderTree(true);
        const res = await axiosInstance.get(`/folders/tree?storeId=${storeId}`);
        setFolderTree(res.data);
        const parentFolderId = findFolderParentId(res.data, folder.id);
        if (parentFolderId !== undefined) {
          setEditFolderForm((prev) => ({ ...prev, parentFolderId }));
        }
      } catch (err) {
        toast.error("Failed to fetch folder tree for selected store");
      } finally {
        setLoadingFolderTree(false);
      }
    }
  };

  // Submit folder edit
  const handleUpdateFolder = async (e) => {
    e.preventDefault();
    if (!editingFolder || loadingEditFolder) return;

    setLoadingEditFolder(true);
    try {
      await axiosInstance.put(`/folders/${editingFolder.id}`, {
        name: editFolderForm.name,
        storeId: editFolderForm.storeId,
        parentFolderId:
          editFolderForm.parentFolderId > 0
            ? editFolderForm.parentFolderId
            : null,
      });

      toast.success("Folder updated successfully");

      // Refresh top-level folders
      fetchMainFolders();

      // If folder modal is open and edited folder belongs to it, refresh the contents
      if (isFolderContentsModalOpen) {
        const updatedContents = await fetchFolderTreeRecursive(openedFolder);
        setOpenedFolderContents(updatedContents);
      }

      handleEditModalClose();
    } catch (err) {
      toast.error(
        err.response?.data?.errors?.[0]?.reason || "Failed to edit folder"
      );
    } finally {
      setLoadingEditFolder(false);
    }
  };

  // Recursive function to fetch folder contents and build tree
  const fetchFolderTreeRecursive = async (folder) => {
    try {
      const res = await axiosInstance.get(`/folders/${folder.id}/contents`, {
        params: {
          ParentFolderId: folder.id,
          storeId: folder.storeId,
          page: 1,
          pageSize: 100000,
          sortColumn: "Name",
          sortDirection: "asc",
        },
      });

      const allItems = res.data.response;
      const subfolders = allItems.filter((item) => item.type === "Folder");
      const courses = allItems.filter((item) => item.type === "Course");

      // For each subfolder, fetch its children recursively
      const nestedSubfolders = await Promise.all(
        subfolders.map(async (sf) => {
          const children = await fetchFolderTreeRecursive(sf);
          return { ...sf, subFolders: children };
        })
      );

      return [...nestedSubfolders, ...courses];
    } catch (err) {
      console.error("Error fetching folder contents:", err);
      return [];
    }
  };

  // Recursive search function
  const findNodeById = (nodes, id) => {
    for (const node of nodes) {
      if (node.id === id) return node;
      if (node.subFolders?.length) {
        const found = findNodeById(node.subFolders, id);
        if (found) return found;
      }
    }
    return null;
  };

  // Open folder modal and build full tree
  const handleOpenFoldersModal = async (folder) => {
    setOpenedFolder(folder);
    setFolderContentsModalOpen(true);
    setLoadingFolderContents(true);
    setSelectedNodeId(null); // reset selection
    setOpenedFolderContents([]); // clear previous contents

    try {
      const tree = await fetchFolderTreeRecursive(folder);
      setOpenedFolderContents(tree);
    } catch (err) {
      toast.error("Failed to load folder contents");
    } finally {
      setLoadingFolderContents(false);
    }
  };

  //HANDLE DELETE CONTENT
  const confirmDelete = () => {
    handleDeleteFolders(selectedFolders);
  };

  const handleDeleteFolders = async (folderIds, fromModal = false) => {
    if (!folderIds || folderIds.length === 0) {
      toast.error("No folders selected for deletion");
      return;
    }

    setIsDeleting(true);

    try {
      // Execute all deletions in parallel
      const deleteRequests = folderIds.map((id) =>
        axiosInstance.delete(`/folders/${id}`)
      );
      await Promise.all(deleteRequests);

      // Update local states
      if (fromModal) {
        // Update modal contents recursively
        const deleteNode = (nodes) =>
          nodes
            .filter((node) => !folderIds.includes(node.id))
            .map((node) => ({
              ...node,
              subFolders: node.subFolders
                ? deleteNode(node.subFolders)
                : node.subFolders,
            }));

        setOpenedFolderContents((prev) => deleteNode(prev));
        setFolderContentsModalOpen(false);
      } else {
        setSelectedFolders([]);
        fetchMainFolders();
      }

      toast.success(
        folderIds.length > 1
          ? `${folderIds.length} folders deleted successfully`
          : "Folder deleted successfully"
      );
    } catch (error) {
      const message =
        error.response?.data?.errors?.[0]?.reason ||
        error.response?.data?.detail ||
        error.message ||
        "Failed to delete folder";
      toast.error(message);
    } finally {
      setIsDeleting(false);
      setConfirmDeleteModalOpen(false);
    }
  };

  const handleCloseAddFolderModal = () => {
    setAnimationClass(
      animationClass.includes("right") ? "slide-out-right" : "slide-out"
    );
    setTimeout(() => {
      setAddFolderForm({
        name: "",
        storeId: 0,
        parentFolderId: 0,
      });

      setFolderTree([]); // <--- reset the tree

      setFolderModalOpen(false);
      setAnimationClass("");
    }, 500);
  };

  const handleEditModalClose = () => {
    // Slide out depending on how it came in
    setAnimationClass(
      animationClass.includes("right") ? "slide-out-right" : "slide-out"
    );

    setTimeout(() => {
      setSelectedNodeId(null);
      setEditingFolder(null);
      setEditFolderForm({ name: "", storeId: 0, parentFolderId: 0 });
      setEditModalOpen(false);
      setAnimationClass("");
    }, 500);
  };

  useEffect(() => {
    if (isFolderContentsModalOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
    } else {
      const scrollY = Math.abs(parseInt(document.body.style.top || "0"));
      document.body.style.position = "";
      document.body.style.top = "";
      window.scrollTo(0, scrollY);
    }
  }, [isFolderContentsModalOpen]);

  return (
    <div
      className={`flex flex-col lg:flex-row lg:items-start ${
        isDarkMode ? "bg-[#292929] text-white" : "bg-white text-black"
      } p-4 lg:p-8 min-h-screen`}
    >
      <div
        className={`${
          isDarkMode ? "bg-[#1e1e1e] text-white" : "bg-white text-black"
        } p-4 lg:p-8 mt-[40px] lg:mt-0 rounded-lg shadow-xl min-w-0 flex-1`}
      >
        {/* Actions */}
        <div className="flex min-w-0 flex-col xl:flex-row xl:justify-between mb-4 gap-3">
          <input
            type="text"
            placeholder="Search Folders"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full min-w-0 xl:w-64 xl:shrink-0 px-4 py-2 border rounded-lg ${
              isDarkMode ? "bg-[#292929]" : "bg-gray-100"
            }`}
          />

          <div className="grid min-w-0 grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap lg:justify-end gap-2 [&>button]:justify-center [&>button]:min-w-0 [&>button>svg]:shrink-0">
            <button
              onClick={handleFolderModalOpen}
              className="bg-green-500 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <IoMdAdd size={20} /> Add Folder
            </button>
            <button
              onClick={() => {
                if (selectedFolders.length > 0) {
                  const folder = folders.find(
                    (f) => f.id === selectedFolders[0]
                  );
                  handleEditFolderModalOpen(folder);
                }
              }}
              disabled={selectedFolders.length === 0}
              className={`flex items-center gap-2 bg-yellow-500 text-white px-4 py-2 rounded-lg ${
                selectedFolders.length === 0
                  ? "opacity-40 cursor-not-allowed"
                  : "hover:bg-yellow-600"
              }`}
            >
              <MdModeEdit size={20} /> Edit Folder
            </button>
            <button
              onClick={() => setConfirmDeleteModalOpen(true)}
              disabled={selectedFolders.length === 0}
              className={`flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg ${
                selectedFolders.length === 0
                  ? "opacity-40 cursor-not-allowed"
                  : "hover:bg-red-600"
              }`}
            >
              <MdOutlineDelete size={20} /> Delete Folder
            </button>

            <div className="relative w-full min-w-0 lg:w-[200px] flex items-center">
              <select
                value={selectedStoreIdTable}
                onChange={(e) =>
                  setSelectedStoreIdTable(Number(e.target.value))
                }
                className={`${
                  isDarkMode
                    ? "border border-customRed text-white bg-transparent"
                    : "border border-customBlue text-gray-700 bg-transparent"
                } px-3 pr-10 py-2 rounded-lg w-full appearance-none`}
              >
                {stores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name}
                  </option>
                ))}
              </select>

              {/* Arrow */}
              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                <svg
                  className={`w-4 h-4 ${
                    isDarkMode ? "text-white" : "text-gray-700"
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Folder Table */}
        <div className="overflow-x-auto h-[650px] custom-scrollbar min-w-0 max-w-full">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    onChange={(e) =>
                      setSelectedFolders(
                        e.target.checked ? folders.map((f) => f.id) : []
                      )
                    }
                    checked={
                      folders.length > 0 &&
                      selectedFolders.length === folders.length
                    }
                  />
                </th>
                <th
                  onClick={() => toggleSortDirection("name")}
                  className="cursor-pointer px-4 py-2 text-center"
                >
                  Name{" "}
                  {sortColumn === "name"
                    ? sortDirection === "asc"
                      ? " ▲"
                      : " ▼"
                    : ""}
                </th>
                <th
                  onClick={() => toggleSortDirection("StoreName")}
                  className="cursor-pointer px-4 py-2 text-center"
                >
                  Store{" "}
                  {sortColumn === "StoreName"
                    ? sortDirection === "asc"
                      ? " ▲"
                      : " ▼"
                    : ""}
                </th>
                <th
                  onClick={() => toggleSortDirection("SubFoldersCount")}
                  className="cursor-pointer px-4 py-2 text-center"
                >
                  Sub Folders{" "}
                  {sortColumn === "SubFoldersCount"
                    ? sortDirection === "asc"
                      ? " ▲"
                      : " ▼"
                    : ""}
                </th>
                {/* <th
                  onClick={() => toggleSortDirection("CoursesCount")}
                  className="cursor-pointer px-4 py-2 text-center"
                >
                  Courses Count{" "}
                  {sortColumn === "CoursesCount"
                    ? sortDirection === "asc"
                      ? " ▲"
                      : " ▼"
                    : ""}
                </th> */}
                <th className="px-4 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-4">
                    Loading...
                  </td>
                </tr>
              ) : folders.length > 0 ? (
                folders.map((folder) => (
                  <tr className="animate-zoomIn" key={folder.id}>
                    <td className="text-center px-4 py-2">
                      <input
                        type="checkbox"
                        checked={selectedFolders.includes(folder.id)}
                        onChange={() =>
                          setSelectedFolders((prev) =>
                            prev.includes(folder.id)
                              ? prev.filter((id) => id !== folder.id)
                              : [...prev, folder.id]
                          )
                        }
                      />
                    </td>
                    <td className="text-center px-4 py-2">{folder.name}</td>
                    <td className="text-center px-4 py-2">
                      {folder.storeName}
                    </td>
                    <td className="text-center px-4 py-2">
                      {folder.subFoldersCount}
                    </td>
                    {/* <td className="text-center px-4 py-2">
                      {folder.totalCourses ?? 0}
                    </td> */}

                    <td className="text-center px-4 py-2">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => handleOpenFoldersModal(folder)}
                        className="px-4 py-2 rounded-xl bg-blue-500 text-white font-medium 
               shadow-md hover:bg-blue-600 transition-colors duration-200"
                      >
                        Open Folder
                      </motion.button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-4">
                    No Folders Available
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Add Folder Modal */}
          {isFolderModalOpen && (
            <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50 p-4 overflow-y-auto">
              <div
                className={` max-h-[calc(100dvh-2rem)] overflow-y-auto max-w-full ${animationClass}     ${
                  isDarkMode ? "bg-gray-300 text-black" : "bg-white text-black"
                } relative flex flex-col p-6 rounded-[35px] shadow-lg w-[500px]   `}
              >
                <IoIosCloseCircleOutline
                  onClick={handleCloseAddFolderModal}
                  className="absolute top-4 right-4 text-2xl cursor-pointer text-gray-500 hover:text-red-500"
                />
                <h2 className="text-2xl font-bold text-center mb-4">
                  Create Folder
                </h2>
                <form
                  onSubmit={handleAddFolder}
                  className="flex flex-col gap-4"
                >
                  <input
                    type="text"
                    placeholder="Folder Name"
                    value={addFolderForm.name}
                    onChange={(e) =>
                      setAddFolderForm({
                        ...addFolderForm,
                        name: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 rounded-xl border border-black border-opacity-20 custom-scrollbar"
                    required
                  />
                  {/* 🏪 Store Selector */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium mb-2">
                      Select Store
                    </label>
                    {/* Add Modal Store Selector */}
                    <select
                      className="w-full px-3 py-2 rounded-xl border border-black border-opacity-20"
                      value={addFolderForm.storeId}
                      onChange={(e) =>
                        handleStoreChange(Number(e.target.value), "add")
                      }
                    >
                      <option value={0}>Select Store</option>
                      {stores.map((store) => (
                        <option key={store.id} value={store.id}>
                          {store.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* 📂 Folder Tree - only show after store is chosen */}
                  <div className="mt-6">
                    <label className="block text-sm font-medium mb-2">
                      Select Folder
                    </label>
                    <div className="border border-gray-300 rounded-lg p-3 bg-gray-50 h-[200px] overflow-y-auto custom-scrollbar">
                      {folderTree && folderTree.length > 0 ? (
                        <TreeView
                          nodes={folderTree}
                          selectedNodeId={addFolderForm.parentFolderId}
                          onSelectNode={(nodeId) =>
                            setAddFolderForm((prev) => ({
                              ...prev,
                              parentFolderId: nodeId,
                            }))
                          }
                          selectable={true}
                        />
                      ) : (
                        <p className="text-gray-400 text-center">
                          {addFolderForm.storeId
                            ? "No folders available for this store."
                            : "Select a store to view folders."}
                        </p>
                      )}
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="bg-[#2264E5] text-white py-2 px-4 rounded-md"
                  >
                    {loadingAddFolder ? "Adding..." : "Add Folder"}
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* Folder Edit Modal */}
          {isEditModalOpen && (
            <div
              className={`fixed inset-0 z-[1100] flex bg-slate-950/65 p-4 backdrop-blur-sm ${
                isFolderContentsModalOpen
                  ? "items-center justify-end lg:pr-6"
                  : "items-center justify-center"
              }`}
            >
              <div
                className={`${animationClass} ${
                  isDarkMode
                    ? "border-white/10 bg-[#191919] text-white"
                    : "border-slate-200 bg-white text-slate-900"
                } relative flex h-[760px] max-h-[calc(100vh-2rem)] w-[512px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-3xl border p-6 shadow-2xl`}
              >
                <IoIosCloseCircleOutline
                  onClick={loadingEditFolder ? undefined : handleEditModalClose}
                  className={`absolute right-4 top-4 text-2xl text-gray-500 ${loadingEditFolder ? "cursor-not-allowed opacity-40" : "cursor-pointer hover:text-red-500"}`}
                />
                <div className="mb-5 pr-8">
                  <span className="text-xs font-bold uppercase tracking-wider text-blue-500">
                    Folder settings
                  </span>
                  <h2 className="mt-1 text-2xl font-bold">Edit Folder</h2>
                  <p className={`mt-1 text-sm ${isDarkMode ? "text-gray-400" : "text-slate-500"}`}>
                    Update its name, store, or parent folder.
                  </p>
                </div>
                <form
                  onSubmit={handleUpdateFolder}
                  className="custom-scrollbar flex min-h-0 w-full flex-1 flex-col gap-4 overflow-x-hidden overflow-y-auto pr-1"
                >
                  <div>
                    <label htmlFor="edit-folder-name" className="mb-2 block text-sm font-semibold">
                      Folder Name
                    </label>
                    <input
                      id="edit-folder-name"
                      type="text"
                      placeholder="Enter folder name"
                      value={editFolderForm.name}
                      onChange={(e) =>
                        setEditFolderForm({
                          ...editFolderForm,
                          name: e.target.value,
                        })
                      }
                      className={`w-full rounded-xl border px-4 py-3 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 ${isDarkMode ? "border-white/10 bg-white/[0.04]" : "border-slate-200 bg-slate-50"}`}
                      required
                    />
                  </div>
                  {/* Edit Modal Store Selector */}
                  <div>
                    <label htmlFor="edit-folder-store" className="mb-2 block text-sm font-semibold">
                      Store
                    </label>
                    <select
                      id="edit-folder-store"
                      className={`w-full rounded-xl border px-4 py-3 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 ${isDarkMode ? "border-white/10 bg-[#242424]" : "border-slate-200 bg-slate-50"}`}
                      value={editFolderForm.storeId}
                      onChange={(e) =>
                        handleStoreChange(Number(e.target.value), "edit")
                      }
                    >
                      <option value={0}>Select Store</option>
                      {stores.map((store) => (
                        <option key={store.id} value={store.id}>
                          {store.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* 📂 Folder Tree - only show after store is chosen */}
                  {editFolderForm.storeId && (
                    <div className="mt-2 shrink-0">
                      <label className="mb-2 block text-sm font-semibold">
                        Parent Folder <span className="font-normal text-gray-400">(Optional)</span>
                      </label>

                      <div
                        className={`custom-scrollbar h-[300px] overflow-y-auto rounded-2xl border p-3 ${isDarkMode ? "border-white/10 bg-white/[0.03]" : "border-slate-200 bg-slate-50"}`}
                      >
                        {loadingFolderTree ? (
                          <div className="text-gray-500 text-sm animate-pulse text-center">
                            Loading folder tree...
                          </div>
                        ) : folderTree.length > 0 ? (
                          <TreeView
                            key={`${editFolderForm.storeId}-${folderTree.length}`}
                            nodes={folderTree}
                            selectedNodeId={editTreeSelection}
                            currentNodeId={editingFolder?.id}
                            onSelectNode={(nodeId) => {
                              if (String(nodeId) === String(editingFolder?.id)) return;
                              setEditTreeSelection(nodeId);
                              setEditFolderForm((prev) => ({
                                ...prev,
                                parentFolderId: nodeId,
                              }));
                            }}
                            selectable
                          />
                        ) : (
                          <div className="text-gray-500 text-sm">
                            No folders found for this store
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loadingEditFolder}
                    className="mt-1 flex min-h-[48px] shrink-0 items-center justify-center gap-2 rounded-xl bg-[#2264E5] px-5 py-3 font-semibold text-white transition hover:bg-blue-600 disabled:cursor-wait disabled:opacity-70 lg:sticky lg:bottom-0"
                  >
                    {loadingEditFolder ? (
                      <>
                        <span
                          className="h-5 w-5 animate-spin rounded-full border-2 border-white/35 border-t-white"
                          aria-hidden="true"
                        />
                        <span>Saving...</span>
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* Folder Contents Modal */}
          {isFolderContentsModalOpen && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4 overflow-y-auto">
              <div
                className={`slide-in relative w-[600px] rounded-2xl shadow-2xl flex flex-col  max-h-[calc(100dvh-2rem)] overflow-y-auto max-w-full ${
                  isDarkMode ? "bg-gray-800 text-white" : "bg-white text-black"
                }   `}
              >
                {/* Close Button */}
                <button
                  onClick={() => setFolderContentsModalOpen(false)}
                  className="absolute top-4 right-4 text-2xl text-red-600 hover:text-red-400 transition-colors z-20"
                >
                  ✖
                </button>

                {/* Header */}
                <div className="px-6 py-4 border-b">
                  <h2 className="text-xl font-bold">
                    {openedFolder?.name || "Folder"} Contents
                  </h2>
                </div>

                {/* Content Area */}
                <div className="px-6 py-4 overflow-y-auto max-h-[65vh] space-y-3">
                  {loadingFolderContents ? (
                    <p className="text-center text-gray-400">Loading...</p>
                  ) : openedFolderContents.length === 0 ? (
                    <p className="text-center text-gray-400">
                      No subfolders or courses found.
                    </p>
                  ) : (
                    <TreeView
                      nodes={openedFolderContents}
                      selectedNodeId={selectedNodeId}
                      onSelectNode={setSelectedNodeId}
                      onUpdateNode={(id, newName) => {
                        const updateNode = (nodes) =>
                          nodes.map((node) => {
                            if (node.id === id)
                              return { ...node, name: newName };
                            if (node.subFolders?.length)
                              return {
                                ...node,
                                subFolders: updateNode(node.subFolders),
                              };
                            return node;
                          });
                        setOpenedFolderContents((prev) => updateNode(prev));
                      }}
                      onEditNode={(id) => {
                        const folder = findNodeById(openedFolderContents, id);
                        if (!folder) return;

                        // Keep the Folder Contents modal open
                        handleEditFolderModalOpen(folder);
                      }}
                      onDeleteNode={(id) => {
                        setFolderIdToDelete(id);
                        setConfirmFolderDeleteModalOpen(true);
                      }}
                    />
                  )}
                </div>

                {/* Bottom Edit Section */}
                {selectedNodeId && (
                  <div className="px-6 py-4 border-t flex justify-end gap-2">
                    <button
                      onClick={() => {
                        const folder = findNodeById(
                          openedFolderContents,
                          selectedNodeId
                        );
                        if (!folder) return;

                        // Close contents modal
                        setFolderContentsModalOpen(false);

                        // Open the Edit Folder modal
                        handleEditFolderModalOpen(folder);
                      }}
                      className="bg-yellow-500 text-white py-2 px-4 rounded-md hover:bg-yellow-600"
                    >
                      Edit
                    </button>

                    <button
                      onClick={() => {
                        setFolderIdToDelete(selectedNodeId);
                        setConfirmFolderDeleteModalOpen(true);
                      }}
                      className="bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {confirmDeleteModalOpen && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4 overflow-y-auto">
              <div className="bg-white dark:bg-gray-800 text-black dark:text-white p-6 rounded-2xl shadow-xl w-96 max-h-[calc(100dvh-2rem)] overflow-y-auto max-w-full">
                <h2 className="text-lg font-bold mb-4">Confirm Deletion</h2>
                <p className="mb-6">
                  Are you sure you want to delete {selectedFolders.length}{" "}
                  {selectedFolders.length > 1 ? "folders" : "folder"} ? This
                  action cannot be undone.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setConfirmDeleteModalOpen(false)}
                    className="px-4 py-2 rounded-md border hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700"
                  >
                    {isDeleting ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          )}
          {confirmFolderDeleteModalOpen && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4 overflow-y-auto">
              <div className="bg-white dark:bg-gray-800 text-black dark:text-white p-6 rounded-2xl shadow-xl w-96 max-h-[calc(100dvh-2rem)] overflow-y-auto max-w-full">
                <h2 className="text-lg font-bold mb-4">Confirm Deletion</h2>
                <p className="mb-6">
                  Are you sure you want to delete this folder? This action
                  cannot be undone.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setConfirmFolderDeleteModalOpen(false)}
                    className="px-4 py-2 rounded-md border hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      await handleDeleteFolders([folderIdToDelete], true);
                      setConfirmFolderDeleteModalOpen(false);
                      setFolderIdToDelete(null);
                    }}
                    className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700"
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end py-4 text-black">
          <label
            htmlFor="pageSize"
            className={`${
              isDarkMode ? "text-white" : "text-gray-700"
            } mr-2 text-sm`}
          >
            Folders per page:
          </label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            {[20, 30, 50].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-4 flex flex-col sm:flex-row justify-between items-center">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 1}
            className={`px-4 py-2 bg-gray-300 text-black rounded-lg ${
              page <= 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-400"
            }`}
          >
            Previous
          </button>
          <span className={"text-lg mt-2 sm:mt-0"}>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= totalPages}
            className={`px-4 py-2 bg-gray-300 text-black rounded-lg ${
              page >= totalPages
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-400"
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminFolders;
